import { useCallback, useEffect, useState, type SetStateAction } from 'react';

export interface ActiveConversationsPreferences {
    groupByStatus: boolean;
    collapsedStatusGroups: Record<string, boolean>;
    sortColumn: string;
    sortDirection: 'ascending' | 'descending';
}

const defaultPreferences: ActiveConversationsPreferences = {
    groupByStatus: true,
    collapsedStatusGroups: {},
    sortColumn: 'lastMessage',
    sortDirection: 'descending',
};

const storageKeyPrefix = 'activeConversations.preferences.';
const legacyAllStorageKey = `${storageKeyPrefix}all`;

const getStorageKey = (teamId: string): string =>
    `${storageKeyPrefix}${teamId}`;

const loadPreferences = (
    storageKey: string
): ActiveConversationsPreferences => {
    try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) return { ...defaultPreferences };

        const parsed = JSON.parse(stored) as Partial<ActiveConversationsPreferences>;

        return {
            groupByStatus:
                typeof parsed.groupByStatus === 'boolean'
                    ? parsed.groupByStatus
                    : defaultPreferences.groupByStatus,
            collapsedStatusGroups:
                parsed.collapsedStatusGroups &&
                typeof parsed.collapsedStatusGroups === 'object'
                    ? parsed.collapsedStatusGroups
                    : {},
            sortColumn:
                typeof parsed.sortColumn === 'string' && parsed.sortColumn
                    ? parsed.sortColumn
                    : defaultPreferences.sortColumn,
            sortDirection:
                parsed.sortDirection === 'ascending' ||
                parsed.sortDirection === 'descending'
                    ? parsed.sortDirection
                    : defaultPreferences.sortDirection,
        };
    } catch (error) {
        console.warn(
            'Unable to load active conversation preferences from localStorage.',
            error
        );
        return { ...defaultPreferences };
    }
};

const savePreferences = (
    storageKey: string,
    preferences: ActiveConversationsPreferences
): void => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
        console.warn(
            'Unable to save active conversation preferences to localStorage.',
            error
        );
    }
};

export const useActiveConversationsPreferences = (
    teamId?: string | null
) => {
    const storageKey = teamId ? getStorageKey(teamId) : null;
    const [preferences, setPreferencesState] = useState(() => ({
        ...defaultPreferences,
    }));
    const [loadedStorageKey, setLoadedStorageKey] = useState<string | null>(
        null
    );
    const isLoaded = storageKey !== null && loadedStorageKey === storageKey;

    useEffect(() => {
        if (!storageKey) {
            setPreferencesState({ ...defaultPreferences });
            setLoadedStorageKey(null);
            return;
        }

        localStorage.removeItem(legacyAllStorageKey);
        setPreferencesState(loadPreferences(storageKey));
        setLoadedStorageKey(storageKey);
    }, [storageKey]);

    const setPreferences = useCallback(
        (update: SetStateAction<ActiveConversationsPreferences>) => {
            setPreferencesState((previous) => {
                const nextPreferences =
                    typeof update === 'function'
                        ? update(previous)
                        : update;
                if (storageKey && isLoaded) {
                    savePreferences(storageKey, nextPreferences);
                }
                return nextPreferences;
            });
        },
        [isLoaded, storageKey]
    );

    return [preferences, setPreferences, isLoaded] as const;
};
